import { useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Menu, X, Phone, MapPin, Clock, Instagram, Star, 
  Shield, Sparkles, Heart, Smile, Sun, CircleCheck, 
  ChevronDown, ChevronRight, Calendar, Mail, User,
  MessageSquare, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import { Toaster, toast } from "sonner";
import { format } from "date-fns";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import AdminDashboard from "@/AdminDashboard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Icon mapping
const iconMap = {
  Shield, Sparkles, Heart, Smile, Sun, CircleCheck, Clock
};

// Navbar Component
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#about", label: "About" },
    { href: "#services", label: "Services" },
    { href: "#doctor", label: "Doctor" },
    { href: "#testimonials", label: "Reviews" },
    { href: "#faq", label: "FAQ" },
    { href: "#contact", label: "Contact" },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-white/90 backdrop-blur-md shadow-soft" 
          : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a 
            href="#hero" 
            onClick={(e) => { e.preventDefault(); scrollToSection("#hero"); }}
            className="flex items-center gap-2"
            data-testid="logo-link"
          >
            <img
              src="/neelkanth-logo.png"
              alt="Neelkanth Dentistry"
              className="h-16 w-16 rounded-full object-cover border border-sage-100 shadow-sm"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="nav-link text-foreground/80 hover:text-primary font-medium"
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </button>
            ))}
            <Button
              onClick={() => scrollToSection("#booking")}
              className="btn-primary"
              data-testid="nav-book-btn"
            >
              Book Appointment
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-primary"
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white/95 backdrop-blur-md rounded-2xl mt-2 shadow-medium overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="py-4 px-4 flex flex-col gap-2">
                {navLinks.map((link) => (
                  <button
                    key={link.href}
                    onClick={() => scrollToSection(link.href)}
                    className="text-left py-3 px-4 rounded-xl text-foreground/80 hover:bg-sage-50 hover:text-primary font-medium transition-colors"
                    data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  >
                    {link.label}
                  </button>
                ))}
                <Button
                  onClick={() => scrollToSection("#booking")}
                  className="btn-primary mt-2"
                  data-testid="mobile-book-btn"
                >
                  Book Appointment
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.nav>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToBooking = () => {
    document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-testid="hero-section">
      {/* Background decorations */}
      <div className="blob blob-green w-96 h-96 -top-20 -right-20 opacity-20" />
      <div className="blob blob-gold w-72 h-72 bottom-20 -left-20 opacity-15" />
      
      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-left"
          >
            <span className="inline-block px-4 py-2 bg-sage-50 text-primary rounded-full text-sm font-medium mb-6">
              Welcome to Neelkanth Dentistry
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              Your Smile Deserves{" "}
              <span className="text-primary">Gentle</span> Care
            </h1>
            <p className="text-lg text-muted-foreground max-w-xl mb-8">
              Experience dentistry that puts your comfort first. We combine modern techniques 
              with a warm, patient-first approach to give you the healthy, confident smile you deserve.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <Button 
                onClick={scrollToBooking}
                className="btn-primary text-lg px-8 py-6"
                data-testid="hero-book-btn"
              >
                Book Appointment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <a 
                href="tel:+917503823584"
                className="btn-secondary text-lg px-8 py-6 flex items-center justify-center gap-2"
                data-testid="hero-call-btn"
              >
                <Phone className="w-5 h-5" />
                Call Clinic
              </a>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-6 items-center text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                <span>10+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-primary" />
                <span>Gentle Approach</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-secondary" />
                <span>5-Star Reviews</span>
              </div>
            </div>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-large">
              <img
                src="https://images.pexels.com/photos/3845985/pexels-photo-3845985.jpeg"
                alt="Happy patient with confident smile"
                className="w-full h-auto object-cover aspect-[4/3]"
                data-testid="hero-image"
              />
              {/* Overlay card */}
              <div className="absolute bottom-6 left-6 right-6 glass-card p-4">
                <p className="text-sm font-medium text-primary mb-1">
                  "Improving lives, one smile at a time"
                </p>
                <p className="text-xs text-muted-foreground">
                  — Our Promise to You
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const features = [
    { icon: Shield, title: "Ethical Practice", desc: "Honest consultations with no unnecessary treatments" },
    { icon: Heart, title: "Patient Comfort", desc: "Gentle care for anxious patients of all ages" },
    { icon: Sparkles, title: "Modern Technology", desc: "Digital scanning, laser dentistry & advanced tools" },
    { icon: CircleCheck, title: "Hygiene First", desc: "Strict sterilization and safety protocols" },
  ];

  const services = [
    "Painless Root Canal Treatments",
    "Crowns & Bridges",
    "Invisible Braces",
    "Veneers & Laminates",
    "Fixed & Partial Dentures",
    "Teeth Cleaning & Polishing",
    "Laser Dentistry",
    "Teeth Whitening",
    "Digital Dental Scanning",
    "Dental Implants",
    "Pediatric Dentistry",
    "Smile Designing",
  ];

  return (
    <section id="about" className="section-padding bg-sage-50/50" data-testid="about-section">
      <div className="container-custom">
        {/* Main About Content */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-16">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="rounded-3xl overflow-hidden shadow-medium">
              <img
                src="https://images.pexels.com/photos/6193192/pexels-photo-6193192.jpeg"
                alt="Modern dental clinic interior"
                className="w-full h-auto object-cover aspect-[4/3]"
                data-testid="about-image"
              />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-6 -right-6 bg-white rounded-2xl p-6 shadow-medium hidden md:block">
              <p className="text-4xl font-serif font-bold text-primary">10+</p>
              <p className="text-sm text-muted-foreground">Years of Trust</p>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-white text-primary rounded-full text-sm font-medium mb-4">
              About Our Clinic
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-6">
              Complete & Advanced Dental Care
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              <strong className="text-foreground">Neelkanth Dentistry</strong> is a leading and trusted dental clinic 
              in Tagore Garden, West Delhi, committed to providing comprehensive, high-quality dental care for 
              patients of all age groups. Our clinic combines clinical expertise with modern technology to deliver 
              comfortable, ethical, and long-lasting dental solutions.
            </p>
            <p className="text-muted-foreground mb-6 leading-relaxed">
              With a strong focus on patient comfort, strict sterilization protocols, and evidence-based dental 
              practices, we strive to create a safe and reassuring environment for every patient. Our commitment 
              to excellence and personalized care makes us a reliable choice for advanced dental treatment in 
              Tagore Garden, New Delhi.
            </p>

            {/* Features grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="flex items-start gap-3 p-4 bg-white rounded-xl"
                >
                  <feature.icon className="w-6 h-6 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">{feature.title}</p>
                    <p className="text-sm text-muted-foreground">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Services Overview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-3xl p-8 lg:p-12 shadow-soft"
        >
          <div className="text-center mb-8">
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-foreground mb-4">
              Full Spectrum of Dental Services
            </h3>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              We offer advanced treatment options including laser dentistry, digital dental scanning, and dental 
              implants — providing precise, minimally invasive, and effective care. Every treatment plan is 
              customized after thorough evaluation, ensuring optimal function, aesthetics, and long-term oral health.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {services.map((service, index) => (
              <motion.div
                key={service}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="flex items-center gap-2 p-3 bg-sage-50 rounded-xl"
              >
                <CircleCheck className="w-4 h-4 text-primary flex-shrink-0" />
                <span className="text-sm text-foreground">{service}</span>
              </motion.div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-primary/5 rounded-2xl border border-primary/10">
            <p className="text-center text-muted-foreground">
              <span className="text-primary font-medium">Specialized services</span> include pediatric dentistry 
              with a gentle, child-friendly approach, oral and maxillofacial surgical procedures, smile designing, 
              and full mouth rehabilitation for complex dental needs.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Doctor Section
const DoctorSection = () => {
  return (
    <section id="doctor" className="section-padding" data-testid="doctor-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-2 lg:order-1"
          >
            <span className="inline-block px-4 py-2 bg-sage-50 text-primary rounded-full text-sm font-medium mb-4">
              👩‍⚕️ Meet Your Dentist
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-2">
              Dr. Surbhi Mishra
            </h2>
            <p className="text-secondary font-medium text-lg mb-2">
              Lead Dental Surgeon
            </p>
            <p className="text-muted-foreground text-sm mb-6">
              BDS | PGDM (Hospital Management) | 10+ Years of Clinical Experience
            </p>
            
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Dr. Surbhi Mishra is a highly experienced and compassionate dental professional, 
              dedicated to delivering ethical, patient-centric dental care with a strong focus 
              on long-term oral health and overall well-being.
            </p>
            
            <p className="text-muted-foreground mb-6 leading-relaxed">
              She completed her Bachelor of Dental Surgery (BDS) in 2015 from Santosh Dental College 
              & Hospital, Ghaziabad, and further strengthened her expertise with an MBA in Public Health 
              from the National Institute of Health and Family Welfare.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                <p className="text-muted-foreground">
                  <strong className="text-foreground">🦷 Clinical Expertise:</strong> General dentistry, 
                  preventive care, pain-managed treatments, and patient education
                </p>
              </div>
              <div className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                <p className="text-muted-foreground">
                  <strong className="text-foreground">🎓 Teaching Experience:</strong> Trained dental assistant 
                  students under PMKVY (Pradhan Mantri Kaushal Vikas Yojana), Government of India
                </p>
              </div>
              <div className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                <p className="text-muted-foreground">
                  <strong className="text-foreground">💙 Approach:</strong> Honest diagnosis, transparent treatment 
                  planning, gentle care, and a calm, reassuring experience for anxious patients
                </p>
              </div>
              <div className="flex items-start gap-3">
                <ChevronRight className="w-5 h-5 text-primary flex-shrink-0 mt-1" />
                <p className="text-muted-foreground">
                  <strong className="text-foreground">📊 Public Health:</strong> Integrates preventive healthcare 
                  strategies and patient safety standards into everyday practice
                </p>
              </div>
            </div>

            <blockquote className="border-l-4 border-secondary pl-4 italic text-muted-foreground">
              "Improving lives, one smile at a time. Every patient is treated with respect, empathy, 
              and personalized care — because dentistry is not just about teeth, but about people."
            </blockquote>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-1 lg:order-2"
          >
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-medium">
                <img
                  src="https://customer-assets.emergentagent.com/job_gentle-dentistry/artifacts/btxuslfo_Screenshot%202026-01-30%20at%2012.31.54%20AM.png"
                  alt="Dr. Surbhi Mishra - Lead Dental Surgeon"
                  className="w-full h-auto object-cover object-top aspect-[3/4] bg-white"
                  data-testid="doctor-image"
                />
              </div>
              {/* Credential badges */}
              <div className="absolute -bottom-4 -left-4 bg-primary text-white rounded-2xl p-4 shadow-medium">
                <p className="text-2xl font-serif font-bold">10+</p>
                <p className="text-sm opacity-90">Years Experience</p>
              </div>
              <div className="absolute -top-4 -right-4 bg-white text-primary rounded-2xl p-3 shadow-medium hidden md:block">
                <p className="text-xs font-medium">BDS | PGDM</p>
                <p className="text-xs text-muted-foreground">Hospital Mgmt</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };
    fetchServices();
  }, []);

  return (
    <section id="services" className="section-padding bg-sage-50/50" data-testid="services-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-white text-primary rounded-full text-sm font-medium mb-4">
            Our Services
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Comprehensive Dental Care
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            From preventive care to smile transformations, we offer a full range of services 
            tailored to your unique needs — all delivered with gentle expertise.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon] || Shield;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="service-card p-6 group"
                data-testid={`service-${service.id}`}
              >
                <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <IconComponent className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-serif text-xl font-semibold text-foreground mb-2">
                  {service.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <p className="text-muted-foreground mb-4">
            Not sure which service you need? We're happy to help.
          </p>
          <a
            href="#booking"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
            data-testid="services-consult-link"
          >
            Book a Free Consultation
            <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
      }
    };
    fetchTestimonials();
  }, []);

  return (
    <section id="testimonials" className="section-padding" data-testid="testimonials-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-sage-50 text-primary rounded-full text-sm font-medium mb-4">
            Patient Reviews
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Trusted by Happy Smiles
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Real reviews from our patients on Google. Your trust is our greatest reward.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.slice(0, 6).map((testimonial, index) => (
            <motion.div
              key={testimonial.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="testimonial-card p-6"
              data-testid={`testimonial-${testimonial.id}`}
            >
              {/* Google badge */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-4 h-4 ${i < testimonial.rating ? 'fill-secondary text-secondary' : 'text-gray-300'}`} 
                    />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground bg-gray-100 px-2 py-1 rounded-full">
                  Google Review
                </span>
              </div>
              
              <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
                "{testimonial.text}"
              </p>
              
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <span className="font-medium text-primary">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-medium text-foreground text-sm">{testimonial.name}</p>
                  <p className="text-xs text-muted-foreground">{testimonial.time}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-10"
        >
          <p className="text-muted-foreground mb-4">
            <span className="text-primary font-medium">Trusted by hundreds of happy smiles</span> in West Delhi.
          </p>
          <a
            href="https://www.google.com/maps/place/Neelkanth+Dentistry"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary font-medium hover:underline"
            data-testid="google-reviews-link"
          >
            See all reviews on Google
            <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// Instagram Section
const InstagramSection = () => {
  const PROFILE_URL = "https://www.instagram.com/neelkanth1408/";
  const instagramPosts = [
    { id: 1, image: "/ig-pregnancy.png", caption: "Dental care during pregnancy — healthy mother, healthy child 🤰" },
    { id: 2, image: "/ig-6reasons.png", caption: "6 signs it's time to visit your dentist" },
    { id: 3, image: "/ig-procedure.png", caption: "Precision dental care by our expert team" },
    { id: 4, image: "/ig-extracted.png", caption: "Extracted & replaced — restoring confident smiles" },
    { id: 5, image: "https://images.unsplash.com/photo-1489278353717-f64c6ee8a4d2?crop=entropy&cs=srgb&fm=jpg&q=85&w=800", caption: "Real smiles, real transformations ✨" },
    { id: 6, image: "https://images.pexels.com/photos/8669944/pexels-photo-8669944.jpeg?auto=compress&cs=tinysrgb&w=800", caption: "Gentle care, every visit" },
  ];

  const [api, setApi] = useState(null);

  useEffect(() => {
    if (!api) return;
    const interval = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, 3500);
    return () => clearInterval(interval);
  }, [api]);

  return (
    <section className="section-padding bg-sage-50/50" data-testid="instagram-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-white text-primary rounded-full text-sm font-medium mb-4">
            Follow Us
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Behind the Scenes
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Follow us on Instagram for dental tips, clinic updates, and smile transformations.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="px-8 sm:px-12 mb-10"
        >
          <Carousel
            setApi={setApi}
            opts={{ align: "start", loop: true }}
            className="w-full"
            data-testid="instagram-carousel"
          >
            <CarouselContent>
              {instagramPosts.map((post) => (
                <CarouselItem
                  key={post.id}
                  className="basis-full sm:basis-1/2 lg:basis-1/3"
                >
                  <a
                    href={PROFILE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="instagram-card aspect-square relative group block overflow-hidden rounded-2xl"
                    data-testid={`instagram-post-${post.id}`}
                  >
                    <img
                      src={post.image}
                      alt={post.caption}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-5">
                      <Instagram className="w-7 h-7 text-white mb-2" />
                      <p className="text-white text-sm font-medium leading-snug">
                        {post.caption}
                      </p>
                    </div>
                  </a>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious data-testid="instagram-carousel-prev" />
            <CarouselNext data-testid="instagram-carousel-next" />
          </Carousel>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <a
            href={PROFILE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 btn-secondary px-6 py-3"
            data-testid="instagram-follow-btn"
          >
            <Instagram className="w-5 h-5" />
            @neelkanth1408
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    const fetchFaqs = async () => {
      try {
        const response = await axios.get(`${API}/faqs`);
        setFaqs(response.data);
      } catch (error) {
        console.error("Error fetching FAQs:", error);
      }
    };
    fetchFaqs();
  }, []);

  return (
    <section id="faq" className="section-padding" data-testid="faq-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-block px-4 py-2 bg-sage-50 text-primary rounded-full text-sm font-medium mb-4">
              FAQs
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-6">
              Common Questions
            </h2>
            <p className="text-muted-foreground mb-8">
              We understand visiting a dentist can raise many questions. Here are answers to the 
              most common ones. Don't see your question? Feel free to contact us.
            </p>
            <img
              src="https://images.pexels.com/photos/3845810/pexels-photo-3845810.jpeg"
              alt="Friendly dental consultation"
              className="rounded-2xl w-full max-w-md hidden lg:block"
              data-testid="faq-image"
            />
          </motion.div>

          {/* Right side - Accordion */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <Accordion type="single" collapsible className="space-y-4" data-testid="faq-accordion">
              {faqs.map((faq) => (
                <AccordionItem 
                  key={faq.id} 
                  value={faq.id}
                  className="bg-white rounded-xl px-6 border-none shadow-sm"
                  data-testid={`faq-item-${faq.id}`}
                >
                  <AccordionTrigger className="text-left font-medium text-foreground hover:text-primary py-5">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Booking Section
const BookingSection = () => {
  const [formData, setFormData] = useState({
    patient_name: "",
    phone: "",
    email: "",
    service: "",
    preferred_date: "",
    preferred_time: "",
    message: "",
  });
  const [date, setDate] = useState(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    "General Checkup",
    "Preventive Care",
    "Tooth-colored Fillings",
    "Root Canal Treatment",
    "Smile Designing",
    "Teeth Whitening",
    "Dentures",
    "Emergency Care",
  ];

  const timeSlots = [
    "9:00 AM", "10:00 AM", "11:00 AM", "12:00 PM",
    "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM", "6:00 PM"
  ];

  const handleDateSelect = (selectedDate) => {
    setDate(selectedDate);
    if (selectedDate) {
      setFormData(prev => ({
        ...prev,
        preferred_date: format(selectedDate, "yyyy-MM-dd")
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.patient_name || !formData.phone || !formData.service || !formData.preferred_date || !formData.preferred_time) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...formData,
        email: formData.email || undefined,
        message: formData.message || undefined,
      };
      await axios.post(`${API}/appointments`, payload);
      toast.success("Appointment request submitted! We'll contact you shortly.");
      setFormData({
        patient_name: "",
        phone: "",
        email: "",
        service: "",
        preferred_date: "",
        preferred_time: "",
        message: "",
      });
      setDate(undefined);
    } catch (error) {
      toast.error("Something went wrong. Please try again or call us directly.");
      console.error("Error submitting appointment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="section-padding bg-primary" data-testid="booking-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="text-white"
          >
            <span className="inline-block px-4 py-2 bg-white/10 rounded-full text-sm font-medium mb-4">
              Book Your Visit
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold mb-6">
              Ready for a Healthier Smile?
            </h2>
            <p className="text-white/80 mb-8 leading-relaxed">
              Take the first step towards better oral health. Fill out the form and we'll 
              get back to you within 24 hours to confirm your appointment.
            </p>

            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-white/60">Call us directly</p>
                  <p className="font-medium">+91 7503823584</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-white/60">Working hours</p>
                  <p className="font-medium">Mon-Fri: 9AM-7PM | Sat: 9AM-5PM</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/10 rounded-full flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm text-white/60">Location</p>
                  <p className="font-medium">AD-66, Tagore Garden, New Delhi</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Booking Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-6 sm:p-8 shadow-large" data-testid="booking-form">
              <h3 className="font-serif text-2xl font-bold text-foreground mb-6">
                Request an Appointment
              </h3>

              <div className="space-y-4">
                {/* Name & Phone */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Your name"
                        value={formData.patient_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, patient_name: e.target.value }))}
                        className="pl-10 h-12 rounded-xl"
                        data-testid="booking-name-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="tel"
                        placeholder="+91 XXXXX XXXXX"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="pl-10 h-12 rounded-xl"
                        data-testid="booking-phone-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email (optional)
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="pl-10 h-12 rounded-xl"
                      data-testid="booking-email-input"
                    />
                  </div>
                </div>

                {/* Service Selection */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Service Required *
                  </label>
                  <Select
                    value={formData.service}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, service: value }))}
                  >
                    <SelectTrigger className="h-12 rounded-xl" data-testid="booking-service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date & Time */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Preferred Date *
                    </label>
                    <div className="border rounded-xl p-3 bg-white">
                      <CalendarComponent
                        mode="single"
                        selected={date}
                        onSelect={handleDateSelect}
                        disabled={(date) => date < new Date() || date.getDay() === 0}
                        className="rounded-md"
                        data-testid="booking-calendar"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">
                      Preferred Time *
                    </label>
                    <Select
                      value={formData.preferred_time}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, preferred_time: value }))}
                    >
                      <SelectTrigger className="h-12 rounded-xl" data-testid="booking-time-select">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {date && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Selected: {format(date, "MMMM d, yyyy")}
                      </p>
                    )}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Additional Notes (optional)
                  </label>
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                    <Textarea
                      placeholder="Any specific concerns or questions?"
                      value={formData.message}
                      onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                      className="pl-10 min-h-[100px] rounded-xl"
                      data-testid="booking-message-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary text-lg py-6"
                  data-testid="booking-submit-btn"
                >
                  {isSubmitting ? "Submitting..." : "Request Appointment"}
                </Button>

                <p className="text-xs text-center text-muted-foreground">
                  We'll contact you within 24 hours to confirm your appointment.
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="section-padding" data-testid="contact-section">
      <div className="container-custom">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block px-4 py-2 bg-sage-50 text-primary rounded-full text-sm font-medium mb-4">
            Visit Us
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl font-bold text-foreground mb-4">
            Find Our Clinic
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Conveniently located in Tagore Garden, West Delhi. Easy access via metro and ample parking nearby.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <div className="bg-white rounded-2xl p-6 shadow-soft">
              <MapPin className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-medium text-foreground mb-2">Address</h3>
              <p className="text-muted-foreground text-sm">
                AD-66, Basement, Tagore Garden,<br />
                New Delhi 110027
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Near Tagore Garden Metro Station
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-soft">
              <Clock className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-medium text-foreground mb-2">Working Hours</h3>
              <div className="text-sm text-muted-foreground space-y-1">
                <p>Monday - Friday: 9:00 AM - 7:00 PM</p>
                <p>Saturday: 9:00 AM - 5:00 PM</p>
                <p>Sunday: Closed</p>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-soft">
              <Phone className="w-8 h-8 text-primary mb-4" />
              <h3 className="font-medium text-foreground mb-2">Get in Touch</h3>
              <div className="text-sm text-muted-foreground space-y-2">
                <a href="tel:+917503823584" className="block hover:text-primary" data-testid="contact-phone">
                  +91 7503823584
                </a>
                <a href="mailto:msurbhi234@gmail.com" className="block hover:text-primary" data-testid="contact-email">
                  msurbhi234@gmail.com
                </a>
              </div>
            </div>
          </motion.div>

          {/* Clinic Photo */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="rounded-2xl overflow-hidden shadow-soft"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_gentle-dentistry/artifacts/wzmlxnlg_Screenshot%202026-01-30%20at%2012.40.54%20AM.png"
              alt="Neelkanth Dentistry Clinic Entrance"
              className="w-full h-full object-cover"
              data-testid="clinic-photo"
            />
          </motion.div>

          {/* Map */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="rounded-2xl overflow-hidden shadow-soft h-[400px] lg:h-auto"
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3501.4397086614936!2d77.10691597613477!3d28.642689083424487!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390d0364c0000001%3A0x5f0d5d5d5d5d5d5d!2sTagore%20Garden%2C%20New%20Delhi%2C%20Delhi!5e0!3m2!1sen!2sin!4v1700000000000!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: "400px" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Clinic Location"
              data-testid="contact-map"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-foreground text-white py-12" data-testid="footer">
      <div className="container-custom">
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {/* Brand */}
          <div>
            <h3 className="font-serif text-2xl font-bold mb-2">
              Neelkanth <span className="text-secondary">Dentistry</span>
            </h3>
            <p className="text-white/70 text-sm mb-4">
              Improving lives, one smile at a time
            </p>
            <a
              href="https://www.instagram.com/neelkanth1408/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
              data-testid="footer-instagram"
            >
              <Instagram className="w-5 h-5" />
              @neelkanth1408
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-medium mb-4">Quick Links</h4>
            <div className="space-y-2 text-sm text-white/70">
              <a href="#about" className="block hover:text-white transition-colors">About Us</a>
              <a href="#services" className="block hover:text-white transition-colors">Services</a>
              <a href="#doctor" className="block hover:text-white transition-colors">Meet the Doctor</a>
              <a href="#booking" className="block hover:text-white transition-colors">Book Appointment</a>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-medium mb-4">Contact</h4>
            <div className="space-y-2 text-sm text-white/70">
              <p>AD-66, Basement, Tagore Garden</p>
              <p>New Delhi 110027</p>
              <a href="tel:+917503823584" className="block hover:text-white transition-colors">
                +91 7503823584
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 text-center">
          <p className="text-sm text-white/50">
            © {currentYear} Neelkanth Dentistry. Your smile is our responsibility.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Main App
function LandingPage() {
  return (
    <div className="min-h-screen bg-cream">
      <Navbar />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <DoctorSection />
        <TestimonialsSection />
        <InstagramSection />
        <FAQSection />
        <BookingSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Toaster richColors position="top-center" />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/admin-bookings" element={<AdminDashboard />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
